import type { AnnualOffer } from '~/utils/annualOffer';
import type { ProOffer } from '~/utils/proOffer';

/** Formule vers laquelle on peut monter (intent envoyé à stripe-b2b). */
export type UpgradeTarget = 'annual' | 'pro' | 'pro_annual';

export type PlanUpgrade = {
    target: UpgradeTarget;
    /** Prix de la formule visée (par mois pour `pro`, par an sinon). */
    price: string;
    /** Même formule payée 12 mois au mois (« 2 mois offerts »), pour les formules à l'année. */
    twelveMonths?: string;
};

/**
 * Montées de formule proposées dans la rubrique Abonnement, chacune ouvrant directement l'écran
 * de confirmation Stripe (une seule page) :
 * - Standard au mois → Standard à l'année, Pro au mois, Pro à l'année ;
 * - Standard à l'année → Pro à l'année ; Pro au mois → Pro à l'année ;
 * - Pro à l'année, ou formule non modifiable en un clic (remise, arrêt programmé, pause…) → rien.
 * Mêmes règles que les encarts existants (`annualOfferFor`, `proOfferFor`), puis filtrées par
 * celles du serveur quand il les donne (`allowed`) : jamais un bouton voué au refus.
 */
export function planUpgradesFor(
    annual: AnnualOffer | null,
    pro: ProOffer | null,
    allowed?: UpgradeTarget[] | null
): PlanUpgrade[] {
    const list: PlanUpgrade[] = [];
    if (annual) list.push({ target: 'annual', price: annual.annual, twelveMonths: annual.twelveMonths });
    if (pro && pro.from !== 'ended') {
        if (pro.monthly) list.push({ target: 'pro', price: pro.monthly.pro });
        list.push({ target: 'pro_annual', price: pro.annual.price, twelveMonths: pro.annual.twelveMonths });
    }
    // `allowed` : formules que le serveur accepterait vraiment (`planOptions.upgrades`).
    return allowed ? list.filter((u) => allowed.includes(u.target)) : list;
}
